/**
 * Dealer public visibility rules (Dealer Profile M2/M4).
 */
import {
  DEALER_PROFILE_PUBLIC_STATUS,
  DEALER_PROFILE_VERIFICATION_STATUS,
} from "../../constants/dealerProfileConstants";
import { isValidDealerSlug } from "./dealerSlug";
import { getDealerProfilePublicState } from "./dealerPublicState";

const BLOCKED_PUBLIC = new Set([
  DEALER_PROFILE_PUBLIC_STATUS.HIDDEN,
  DEALER_PROFILE_PUBLIC_STATUS.INACTIVE,
  DEALER_PROFILE_PUBLIC_STATUS.SUSPENDED,
  DEALER_PROFILE_PUBLIC_STATUS.REJECTED,
  DEALER_PROFILE_PUBLIC_STATUS.DELETED,
]);

const BLOCKED_VERIFICATION = new Set([
  DEALER_PROFILE_VERIFICATION_STATUS.REJECTED,
  DEALER_PROFILE_VERIFICATION_STATUS.SUSPENDED,
]);

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 */
export function isDealerPubliclyVisible(dealer) {
  if (!dealer) return false;
  if (BLOCKED_PUBLIC.has(dealer.publicStatus)) return false;
  if (BLOCKED_VERIFICATION.has(dealer.verificationStatus)) return false;
  if (dealer.publicStatus === DEALER_PROFILE_PUBLIC_STATUS.PUBLIC) return true;
  return false;
}

/**
 * @param {import('./dealerProfileTypes').DealerProfile|null|undefined} dealer
 * @param {{ listings?: object[] }} [options]
 */
export function shouldShowDealerProfile(dealer, options = {}) {
  if (!dealer) return false;
  if (!isValidDealerSlug(dealer.slug)) return false;
  return getDealerProfilePublicState(dealer, options).canShowProfile;
}

export { getDealerProfilePublicState } from "./dealerPublicState";

/**
 * Map backend dealer.status + flags to canonical publicStatus.
 * @param {object} raw
 */
export function normalizeDealerPublicStatus(raw = {}) {
  const status = String(raw.publicStatus || raw.dealerPublicStatus || "").toLowerCase();
  const canonical = Object.values(DEALER_PROFILE_PUBLIC_STATUS);
  if (canonical.includes(status)) return status;

  const dealerStatus = String(raw.status || raw.dealerStatus || "").toLowerCase();
  if (dealerStatus === "suspended") return DEALER_PROFILE_PUBLIC_STATUS.SUSPENDED;
  if (dealerStatus === "rejected") return DEALER_PROFILE_PUBLIC_STATUS.REJECTED;
  if (dealerStatus === "archived" || dealerStatus === "deleted") {
    return DEALER_PROFILE_PUBLIC_STATUS.DELETED;
  }
  if (dealerStatus === "inactive" || dealerStatus === "paused") {
    return DEALER_PROFILE_PUBLIC_STATUS.INACTIVE;
  }

  const profileEnabled =
    raw.publicProfileEnabled ?? raw.dealerPublicProfileEnabled ?? raw.publicProfile ?? null;
  if (profileEnabled === false) return DEALER_PROFILE_PUBLIC_STATUS.HIDDEN;

  if (dealerStatus === "active") return DEALER_PROFILE_PUBLIC_STATUS.PUBLIC;
  if (dealerStatus === "onboarding" && profileEnabled === true) {
    return DEALER_PROFILE_PUBLIC_STATUS.PUBLIC;
  }

  return DEALER_PROFILE_PUBLIC_STATUS.UNKNOWN;
}

/**
 * @param {object} raw
 */
export function normalizeDealerVerificationStatus(raw = {}) {
  const explicit = String(raw.verificationStatus || raw.dealerVerificationStatus || "").toLowerCase();
  const canonical = Object.values(DEALER_PROFILE_VERIFICATION_STATUS);
  if (canonical.includes(explicit)) return explicit;

  if (raw.dealerVerified === true || raw.isVerified === true) {
    return DEALER_PROFILE_VERIFICATION_STATUS.VERIFIED;
  }
  if (raw.dealerVerified === false) return DEALER_PROFILE_VERIFICATION_STATUS.UNVERIFIED;

  const dealerStatus = String(raw.status || raw.dealerStatus || "").toLowerCase();
  if (dealerStatus === "suspended") return DEALER_PROFILE_VERIFICATION_STATUS.SUSPENDED;
  if (dealerStatus === "rejected") return DEALER_PROFILE_VERIFICATION_STATUS.REJECTED;

  return DEALER_PROFILE_VERIFICATION_STATUS.UNKNOWN;
}
